import dynamic from "next/dynamic";
import { getInnerWindowSize } from "@/common/utils/sharedFunctions";
import { getSeasonsLabel } from "@/common/utils/productFunctions";
import { getSortedCart, getSortedWholeCart } from "@/common/utils/cartFunction";
import { toast, Slide } from "react-toastify";
import { useRouter } from "next/router";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "next-i18next";
// BOOTSTRAP
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
// COMPONENT
import CarouselProduct from "../CarouselProduct/CarouselProduct";
import { toast_Success } from "@/components/ui/Toast/toast";
// CONTEXT
import { useApp } from "@/components/context/AppContext/AppContext";
import { useAuth } from "../../../auth/hooks/useAuth";
import { useProductCart, useSetProductCart } from "@/components/context/ProductCartContext/ProductCartContext";
// COMPONENTS
const ColorGrid = dynamic(() => import("@/components/core/ProductPage/ColorGrid/ColorGrid"), { ssr: false });
const FavouritesLogo = dynamic(() => import("@/components/context/FavouritesContext/FavouritesLogo"), { ssr: false });
const LoginSignupButtons = dynamic(() => import("@/components/ui/LoginSignupButtons/LoginSignupButtons"), { ssr: false });
const MoreDetailsPanel = dynamic(() => import("@/components/core/ProductPage/MoreDetailsPanel/MoreDetailsPanel"), { ssr: false });
const PriceTag = dynamic(() => import("@/components/core/ProductPage/PriceTag/PriceTag"), { ssr: false });
const SizeGrid = dynamic(() => import("@/components/core/ProductPage/SizeGrid/SizeGrid"), { ssr: false });
const ToastAddedProduct = dynamic(() => import("@/components/elements/ToastAddedProduct/ToastAddedProduct"), { ssr: false });
// ICONS
import { DeleteIconCross, ShoppingbagIcon } from "@/public/icons/icons";
// STYLE
import fallbackTranslation from "@/lib/dbTranslations";
import s from "./ProductDetail.module.scss";
import { ProductStatus } from "../../Classes/ProductStatus";
import useLocalStorage from "@/common/hooks/useLocalStorage";
import CustomModal from "@/components/portals/CustomModal/CustomModal";
import { useModal } from "@/components/context/ModalContext";
import { ProductionStatus } from "../../Classes/ProductionStatus";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import { Badge } from "react-bootstrap";
import { isBefore, subDays } from "date-fns";

// Display production status of product
function ProductionStatusProduct({ t, product }) {
  switch (product.StatoProduzione != undefined) {
    case ProductionStatus.find((status) => status.key == "PRODUCING").id == product.StatoProduzione:
      return (
        <div className="d-grid">
          <span className="fw-semibold text-warning">
            {t("common." + ProductionStatus.find((status) => status.id == product.StatoProduzione).key.toLowerCase(), { ns: "common" })}
          </span>
          <hr />
        </div>
      );
    case ProductionStatus.find((status) => status.key == "PRODUCED").id == product.StatoProduzione:
      return (
        <div className="d-grid">
          <span className="fw-semibold text-success">
            {t("common." + ProductionStatus.find((status) => status.id == product.StatoProduzione).key.toLowerCase(), { ns: "common" })}
          </span>
          <hr />
        </div>
      );
    case ProductionStatus.find((status) => status.key == "NOT_PRODUCED").id == product.StatoProduzione:
      return (
        <div className="d-grid">
          <span className="fw-semibold text-danger">
            {t("common." + ProductionStatus.find((status) => status.id == product.StatoProduzione).key.toLowerCase(), { ns: "common" })}
          </span>
          <hr />
        </div>
      );

    default:
      return null;
  }
}

// Custom close button used in toasts
const CloseButton = ({ closeToast }) => (
  <Button
    variant="link"
    className="closeButton d-flex "
    style={{ height: "2rem", paddingTop: "0.5rem" }}
    onClick={(e) => {
      e.stopPropagation();
      closeToast();
    }}
  >
    <DeleteIconCross width="18" height="18" color="#000" />
  </Button>
);

const ProductHeader = ({ auth, product }) => {
  return (
    <>
      <div className="d-flex justify-content-between">
        <div>
          <h1 className="fs-5 fw-semibold">{product?.DescrizioneArticolo}</h1>
          <div className="d-flex">
            <h2 className="fs-7 pb-2">{product?.CodiceArticolo}</h2>
            {auth.isUserAdmin() && <h2 className="fs-7 mb-1 ps-1">/ {product?.CodiceFornitore}</h2>}
          </div>
        </div>

        {ProductStatus.find((status) => status.key == "IN_CATALOGUE").id == product?.["Stato"] && (
          <div style={{ maxWidth: "2rem", display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
            <FavouritesLogo product={product} />
          </div>
        )}
      </div>
      {auth.user && (
        <div className="pb-1">
          <h2 className="fs-5">
            <PriceTag product={product} isProductPage={true} />
          </h2>
        </div>
      )}
    </>
  );
};

const SummaryPriceQuantity = ({ t, totalQuantity, product, discountedProductPrice, productPrice, productAmount }) => {
  if (totalQuantity === 0) {
    return <></>;
  } else {
    return (
      <>
        <Col>
          <span>
            {t("unit-price")}: {product?.["Sconto"] ? discountedProductPrice.toFixed(2) + " €" : productPrice.toFixed(2) + " €"}
          </span>
        </Col>
        <Col>
          <span>
            {t("total-quantity")}: {totalQuantity}
          </span>
        </Col>
        <Col>
          <span>
            {t("total-price")}: {productAmount.toFixed(2) + " €"}
          </span>
        </Col>
      </>
    );
  }
};

const BuyButtons = ({ t, product, active, onSubmitProductCart, setActive }) => {
  if (
    ProductStatus.find((status) => status.key == "IN_CATALOGUE").id == product?.["Stato"] ||
    (ProductStatus.find((status) => status.key == "PRESALE").id == product?.["Stato"] && product?.presaleKit?.["active"] == true)
  ) {
    return product.Colori.length && product.Colori.length != "0" && product.Taglie.length && product.Taglie.length != "0" ? (
      <>
        <Button
          type="button"
          disabled={!active}
          className={`p-2 ms-1 border-dark`}
          variant="success  w-100"
          onClick={() => {
            onSubmitProductCart();
            setActive(false);
          }}
        >
          <span className="pe-3">
            <ShoppingbagIcon />
          </span>
          <span>{t("add").toUpperCase()}</span>
        </Button>
      </>
    ) : null;
  } else if (ProductStatus.find((status) => status.key == "OUT_CATALOGUE").id == product?.["Stato"]) {
    return (
      <Button disabled className={`w-100 ${s.out_of_catalog_button}`}>
        <span className="fs-7 fw-semibold">{t("out-of-catalogue").toUpperCase()}</span>
      </Button>
    );
  } else {
    return (
      <Button disabled className={`w-100 ${s.out_of_catalog_button}`}>
        <span className="fs-7 fw-semibold">{t("not-available").toUpperCase()}</span>
      </Button>
    );
  }
};

export default function ProductDetail({ product }) {
  const auth = useAuth();
  const { t } = useTranslation(["product-detail", "common", "db-translations"]);
  const router = useRouter();
  const app = useApp();

  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);

  // Custom modal context state
  const { showModal, hideModal } = useModal();

  let windowSize = useRef({ height: 0, width: 0 });
  useEffect(() => {
    windowSize.current = getInnerWindowSize();
  }, []);

  // ProductCart Context
  const productCart = useProductCart();
  const setProductCart = useSetProductCart();

  let newDate = new Date();

  const [selectedColor, setSelectedColor] = useState("");

  // Size state - object {quantity: itemQuantity, size: selectedSize}
  const [quantityPerSize, setQuantityPerSize] = useState([]);

  // Initialize the style with a custom css class
  const [active, setActive] = useState(false);

  // Set colors and sizes from app context, check if values are ready.
  useEffect(() => {
    if (app.appConfig?.sizes && app.appConfig?.colors) {
      setColors(app.appConfig?.colors);
      setSizes(app.appConfig?.sizes);
    }
  }, [app]);

  // Select first color of already selected colors on ColorGrid when entering a product if it is found in cart.
  // EDIT 12/05/2022 : Added router.query.product as useEffect condition, (Reset some state when changing route)
  // removed productCart from useEffect condition. (useEffect will not be triggered after user add quantities to the product)
  useEffect(() => {
    let found = productCart.find((item) => item.productId === product["_id"] && item.selectedItemQuantity !== undefined);
    if (found) {
      setQuantityPerSize(found.selectedItemQuantity);
      setSelectedColor(found.selectedItemQuantity[0].color);
    } else {
      setQuantityPerSize([]);
      setSelectedColor("");
    }
    // Reset button to default style and state
    setActive(false);
  }, [product, router.query.product]);

  // Toast for added product
  const displayAddedToast = () => {
    if (windowSize.current.width < 991.98) {
      toast_Success(t("product-added"));
    } else {
      toast(<ToastAddedProduct product={product} totalQuantity={totalQuantity} productAmount={productAmount} />, {
        closeButton: CloseButton,
        bodyClassName: "m-0 py-0",
        hideProgressBar: true,
        transition: Slide,
        style: { color: "black", paddingTop: 0, paddingBottom: 0, paddingRight: 0 },
      });
    }
  };

  // Disable form when product is in presale and modify button quantity
  let presaleMode;
  let actionButtonNumber;
  if (ProductStatus.find((status) => status.key == "PRESALE").id == product["Stato"]) {
    actionButtonNumber = product["Confezione"] || 1;
    presaleMode = true;
  } else {
    actionButtonNumber = 1;
    presaleMode = false;
  }

  // Update quantityPerSize state, adding quantity and selected size
  const onChangeQuantity = ({ quantity: inputQuantity, size: selectedSize }) => {
    let currentQuantityPerSize = quantityPerSize;
    // If same selectedColor and selectedSize must update instead of plain add
    if (
      quantityPerSize.filter((item) => {
        return item.color == selectedColor && item.size == selectedSize;
      })?.length > 0
    ) {
      // gets the current quantity removing duplication
      currentQuantityPerSize = quantityPerSize.filter((item) => {
        return item.color !== selectedColor || item.size !== selectedSize;
      });
    }

    //  User has typed 0
    if (inputQuantity == 0) {
      return setQuantityPerSize(
        presaleMode ? [...currentQuantityPerSize.filter((item) => item.color != selectedColor)] : [...currentQuantityPerSize]
      );
    }

    // Initialized new quantity size array
    let updatedQuantityPerSize = [{ quantity: inputQuantity, color: selectedColor, size: selectedSize, notAvailable: false }];
    // Check if presale mode and update each size with current quantity
    if (presaleMode) {
      updatedQuantityPerSize = [];
      currentQuantityPerSize = currentQuantityPerSize.filter((item) => item.color != selectedColor);
      product.Taglie.map((taglia) => {
        updatedQuantityPerSize.push({ quantity: inputQuantity, color: selectedColor, size: taglia, notAvailable: false });
      });
    }

    // Update "quantity per size" array (array of objects with 3 values (quantity, color, size))
    setQuantityPerSize(getSortedCart([...currentQuantityPerSize, ...updatedQuantityPerSize], colors, sizes));
    // Change style of buy button to reflect changes in selected quantity
    setActive(true);
  };

  const onSubmitProductCart = () => {
    // Check if products added to cart are of type "PRESALE" or type "IN_CATALOGUE"
    const presale = localStorage.getItem("presale");

    // Do not add a product in the cart if there's already a product of different type ("PRESALE" or "IN_CATALOGUE")
    if (presale == "true" && ProductStatus.find((status) => status.key == "IN_CATALOGUE").id == product?.["Stato"]) {
      showModal(CustomModal, {
        closeButton: true,
        message: t("cannot-add-catalog"),
        withActionButton: true,
        buttonQuantity: 1,
        buttonText: [t("go-to-cart")],
        buttonAction: () => router.push("/cart"),
      });
      return;
    } else if (presale == "false" && ProductStatus.find((status) => status.key == "PRESALE").id == product?.["Stato"]) {
      showModal(CustomModal, {
        closeButton: true,
        message: t("cannot-add-presale"),
        withActionButton: true,
        buttonQuantity: 1,
        buttonText: [t("go-to-cart")],
        buttonAction: () => router.push("/cart"),
      });
      return;
    }

    // Added return false to prevent toast display.
    if (!active) return false;
    let itemsToSpread = productCart;

    let itemToUpdate = productCart.find((item) => {
      return item.productId === product["_id"];
    });

    // If the same product already exist will return true (length > 0)
    if (itemToUpdate) {
      // Set itemsToSpread equal to all productCart without the selected product
      itemsToSpread = productCart.filter((item) => {
        return item.productId !== product["_id"];
      });
    }

    // Update selectedItemQuantity for this product, this is used only when user put 0 in all sizes fields (product removed).
    if (quantityPerSize?.length == 0) {
      let itemsToSpread = productCart.filter((item) => {
        return item.productId !== product["_id"];
      });
      setProductCart(itemsToSpread);
      toast_Success(t("product-removed"));
      return false;
    } else {
      // Update cart context with updated quantity per size, sorted by color and sizes.
      setProductCart(
        getSortedWholeCart([
          ...itemsToSpread,
          {
            productId: product["_id"],
            selectedItemQuantity: quantityPerSize,
            creationDate: itemToUpdate?.creationDate || newDate,
          },
        ])
      );
      displayAddedToast();

      // If added product is of type PRESALE, add presale field to true in locale storage
      if (ProductStatus.find((status) => status.key == "PRESALE").id == product?.["Stato"]) {
        localStorage.setItem("presale", JSON.stringify(true));
        localStorage.setItem("deliveryDate", '' + addDays(new Date(product?.presaleKit?.endDate), (product?.presaleKit?.producingDays || 0)));

        // If added product is of type CATALOGUE, add presale field to false in locale storage
      } else if (ProductStatus.find((status) => status.key == "IN_CATALOGUE").id == product?.["Stato"]) {
        localStorage.setItem("presale", JSON.stringify(false));
        localStorage.removeItem("deliveryDate");
      }
      return true;
    }
  };

  // Formatted price, displayed only if user is auth
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [productAmount, setProductAmount] = useState(0);

  const productPrice = product?.["Prezzo"];
  const discountedProductPrice = productPrice - productPrice * (product?.["Sconto"] / 100);

  // Calculate total items quantity
  useEffect(() => {
    setTotalQuantity(quantityPerSize.reduce((a, b) => a + b.quantity, 0));
  }, [quantityPerSize]);

  // Calculate total price, based on items quantity and item price
  useEffect(() => {
    if (product?.Sconto) {
      setProductAmount(quantityPerSize.reduce((a, b) => a + b.quantity, 0) * discountedProductPrice);
    } else {
      setProductAmount(quantityPerSize.reduce((a, b) => a + b.quantity, 0) * productPrice);
    }
  }, [totalQuantity]);

  // Get season label and year
  let season;
  let seasonLabel;
  let seasonYear;
  if (product.Stagione) {
    season = getSeasonsLabel(product?.Stagione)?.split(" ");
    seasonLabel = season ? season[0] : "";
    seasonYear = season ? season[1] : "";
  }

  return (
    <>
      <Col className="px-0 px-md-3">
        {/* Mobile view of product title (name, code, price and favourite button)*/}
        <div className={s.carousel_wrapper}>
          <CarouselProduct product={product} selectedColor={selectedColor} />
        </div>
      </Col>
      <Col className={`${s.right_col} px-0 px-sm-2 px-md-3 px-lg-4 pt-3 pt-md-0`}>
        {isBefore(subDays(new Date(), 2), new Date(product["DataInserimento"])) && <div className="d-flex py-2"><Badge bg="black">{t("common.new",{ ns: "common" })}</Badge></div>} 
        <ProductHeader auth={auth} product={product} />
        <div className="py-3">
          {product?.DescrizioneAggiuntiva != undefined && (
            <div className="d-grid">
              <span className="fs-6 fw-semibold">{t("product-description")} </span>
              <span className="fw-medium">{product?.["DescrizioneAggiuntiva"]}</span>
              <hr />
            </div>
          )}
          {product?.Composizione != undefined && (
            <div className="d-grid">
              <span className="fs-6 fw-semibold">{t("composition")} </span>
              <span className="fw-medium">{product?.["Composizione"]}</span>
              <hr />
            </div>
          )}
          {product?.Stagione != undefined && product?.Stagione != '' && (
            <div className="d-grid">
              <span className="fs-6 fw-semibold">{t("common.collection", { ns: "common" })} </span>
              <span className="fw-medium">{t(`season.${seasonLabel}`, { ns: "common" }) + " " + seasonYear}</span>
              <hr />
            </div>
          )}
          {product?.presaleKit?.producingDays != undefined && (
            <div className="d-grid">
              <span className="fs-6 fw-semibold">{`${t("estimated-delivery")} ${product?.presaleKit?.code}`} </span>
              <span className="fw-medium">{`${format(addDays(new Date(product?.presaleKit?.endDate), (product?.presaleKit?.producingDays)), "dd/MM/yyyy")}`}</span>
              <hr />
            </div>
          )}
          {product?.Confezione != undefined && (
            <div className="d-grid">
              <span className="fw-medium">{`${t("pack-of")} ${product.Confezione}`}</span>
              <hr />
            </div>
          )}
          {product?.PrezzoConsigliato != undefined && (
            <div className="d-grid">
              <span className="fw-medium">{`${t("suggested-selling-price")} ${product.PrezzoConsigliato}€`}</span>
              <hr />
            </div>
          )}
          {product?.StatoProduzione != undefined && <ProductionStatusProduct product={product} t={t} />}
        </div>
        {auth.user ? (
          <>
            <div className={`${s.color_selector_container}`}>
              <div className="px-4">
                <div className="d-flex">
                  <p className="pe-2 pt-5 fw-medium">
                    {product?.["Colori"]?.length === 0 ? (
                      <span className="text-danger">{t("no-color-available").toUpperCase()}</span>
                    ) : (
                      <>
                        <span>{t("common:common.color")}:</span>
                        <span className="ps-2 fw-semibold text-danger">
                          {selectedColor == ""
                            ? t("common:common.select-color")
                            : fallbackTranslation("colors", "db-translations", { key: selectedColor }, t, router.locale)}
                        </span>
                      </>
                    )}
                  </p>
                </div>
                <ColorGrid product={product} selected={selectedColor} setSelected={setSelectedColor} />
                <div className="d-flex flex-column" style={{ maxWidth: "30rem" }}>
                  {product?.["Taglie"]?.length === 0 && <span className="fw-medium text-danger">{t("no-size-available").toUpperCase()}</span>}
                  <SizeGrid
                    selectedColor={selectedColor}
                    product={product}
                    onChangeQuantity={onChangeQuantity}
                    quantityPerSize={quantityPerSize}
                    actionButtonNumber={actionButtonNumber}
                    disableForm={presaleMode}
                  />
                </div>
              </div>
              <Row className="mx-0 py-3 ps-1 fs-7">
                <SummaryPriceQuantity
                  t={t}
                  totalQuantity={totalQuantity}
                  product={product}
                  discountedProductPrice={discountedProductPrice}
                  productPrice={productPrice}
                  productAmount={productAmount}
                />
              </Row>
              <MoreDetailsPanel
                cartMode={false}
                modifyQuantity={false}
                price={product?.["Prezzo"]}
                product={product}
                quantityPerSize={quantityPerSize}
                setQuantityPerSize={setQuantityPerSize}
                totalQuantity={totalQuantity}
                variant={"dark"}
              />
            </div>
            <div className="py-5 d-flex">
              <BuyButtons t={t} product={product} active={active} onSubmitProductCart={onSubmitProductCart} setActive={setActive} />
            </div>
          </>
        ) : (
          <div className={s.login_signup_buttons_container}>
            <LoginSignupButtons text={t("login-to-unlock")} />
          </div>
        )}
      </Col>
    </>
  );
}
